import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowRight, ArrowLeft, Check, Users, Heart, MapPin, Megaphone,
  Mail, Phone, Building2, Briefcase, GraduationCap, Globe,
  CheckCircle2, Loader2, ChevronRight, Sparkles, Target, HandHeart
} from 'lucide-react';

const JoinPage = () => {
  const { language } = useLanguage();
  const { isDark } = useTheme();
  
  // Multi-step state
  const [currentStep, setCurrentStep] = useState(0); // 0 = path selection
  const [selectedPath, setSelectedPath] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');
  
  // Form data
  const [formData, setFormData] = useState({
    // Common fields
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    city: '',
    country: "Côte d'Ivoire",
    // Path-specific fields
    profession: '',
    skills: [],
    availability: '',
    motivation: '',
    experience: '',
    // For circle creators
    proposedCity: '',
    hasNetwork: false,
    networkSize: '',
    // For donors
    donationType: 'monthly',
    donationAmount: '',
    // Newsletter
    newsletter: true
  });

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // Translations
  const t = {
    fr: {
      pageTitle: 'Rejoindre le mouvement',
      pageSubtitle: 'Choisissez comment vous souhaitez vous engager',
      paths: {
        volunteer: {
          title: 'Bénévole',
          desc: 'Donnez de votre temps pour faire avancer le projet',
          icon: HandHeart,
          color: '#5B2D8E'
        },
        circleCreator: {
          title: 'Créateur de cercle',
          desc: 'Lancez une section ADA dans votre ville',
          icon: MapPin,
          color: '#0D9488'
        },
        ambassador: {
          title: 'Ambassadeur',
          desc: 'Portez la voix de l\'ADA dans votre réseau',
          icon: Megaphone,
          color: '#F7C948'
        },
        supporter: {
          title: 'Sympathisant',
          desc: 'Restez informé et soutenez à votre rythme',
          icon: Heart,
          color: '#E11D48'
        }
      },
      steps: {
        path: 'Parcours',
        identity: 'Identité',
        details: 'Détails',
        confirm: 'Confirmation'
      },
      form: {
        firstName: 'Prénom',
        lastName: 'Nom',
        email: 'Email',
        phone: 'Téléphone',
        city: 'Ville',
        country: 'Pays',
        profession: 'Profession',
        skills: 'Compétences',
        skillsPlaceholder: 'Ex: Communication, Design, Juridique...',
        availability: 'Disponibilité',
        availabilityOptions: ['Quelques heures/semaine', 'Un jour/semaine', 'Flexible', 'Week-ends uniquement'],
        motivation: 'Pourquoi souhaitez-vous rejoindre l\'ADA ?',
        motivationPlaceholder: 'Partagez votre motivation...',
        experience: 'Expérience associative ou politique',
        experiencePlaceholder: 'Décrivez votre parcours...',
        proposedCity: 'Ville où créer le cercle',
        hasNetwork: 'J\'ai déjà un réseau local mobilisable',
        networkSize: 'Taille approximative du réseau',
        networkSizeOptions: ['5-10 personnes', '10-25 personnes', '25-50 personnes', '50+ personnes'],
        newsletter: 'Je souhaite recevoir les actualités de l\'ADA',
        required: 'Champs obligatoires'
      },
      buttons: {
        next: 'Continuer',
        back: 'Retour',
        submit: 'Envoyer ma candidature',
        submitting: 'Envoi en cours...',
        choosePath: 'Choisir ce parcours'
      },
      success: {
        title: 'Bienvenue dans le mouvement !',
        subtitle: 'Votre candidature a été enregistrée',
        message: 'Un membre de l\'équipe ADA vous contactera dans les prochains jours pour discuter de votre engagement.',
        cta: 'Retour à l\'accueil',
        share: 'Partager mon engagement'
      },
      errors: {
        submit: 'Une erreur est survenue. Veuillez réessayer.',
        required: 'Ce champ est requis'
      }
    },
    en: {
      pageTitle: 'Join the movement',
      pageSubtitle: 'Choose how you want to get involved',
      paths: {
        volunteer: {
          title: 'Volunteer',
          desc: 'Give your time to advance the project',
          icon: HandHeart,
          color: '#5B2D8E'
        },
        circleCreator: {
          title: 'Circle Creator',
          desc: 'Start an ADA section in your city',
          icon: MapPin,
          color: '#0D9488'
        },
        ambassador: {
          title: 'Ambassador',
          desc: 'Carry the voice of ADA in your network',
          icon: Megaphone,
          color: '#F7C948'
        },
        supporter: {
          title: 'Supporter',
          desc: 'Stay informed and support at your own pace',
          icon: Heart,
          color: '#E11D48'
        }
      },
      steps: {
        path: 'Path',
        identity: 'Identity',
        details: 'Details',
        confirm: 'Confirmation'
      },
      form: {
        firstName: 'First name',
        lastName: 'Last name',
        email: 'Email',
        phone: 'Phone',
        city: 'City',
        country: 'Country',
        profession: 'Profession',
        skills: 'Skills',
        skillsPlaceholder: 'Ex: Communication, Design, Legal...',
        availability: 'Availability',
        availabilityOptions: ['A few hours/week', 'One day/week', 'Flexible', 'Weekends only'],
        motivation: 'Why do you want to join ADA?',
        motivationPlaceholder: 'Share your motivation...',
        experience: 'Associative or political experience',
        experiencePlaceholder: 'Describe your background...',
        proposedCity: 'City to create the circle',
        hasNetwork: 'I already have a local network to mobilize',
        networkSize: 'Approximate network size',
        networkSizeOptions: ['5-10 people', '10-25 people', '25-50 people', '50+ people'],
        newsletter: 'I want to receive ADA news',
        required: 'Required fields'
      },
      buttons: {
        next: 'Continue',
        back: 'Back',
        submit: 'Send my application',
        submitting: 'Sending...',
        choosePath: 'Choose this path'
      },
      success: {
        title: 'Welcome to the movement!',
        subtitle: 'Your application has been registered',
        message: 'An ADA team member will contact you in the coming days to discuss your commitment.',
        cta: 'Back to home',
        share: 'Share my commitment'
      },
      errors: {
        submit: 'An error occurred. Please try again.',
        required: 'This field is required'
      }
    },
    pt: {
      pageTitle: 'Aderir ao movimento',
      pageSubtitle: 'Escolha como deseja participar',
      paths: {
        volunteer: {
          title: 'Voluntário',
          desc: 'Dê o seu tempo para avançar o projeto',
          icon: HandHeart,
          color: '#5B2D8E'
        },
        circleCreator: {
          title: 'Criador de círculo',
          desc: 'Inicie uma secção ADA na sua cidade',
          icon: MapPin,
          color: '#0D9488'
        },
        ambassador: {
          title: 'Embaixador',
          desc: 'Leve a voz da ADA à sua rede',
          icon: Megaphone,
          color: '#F7C948'
        },
        supporter: {
          title: 'Simpatizante',
          desc: 'Mantenha-se informado e apoie ao seu ritmo',
          icon: Heart,
          color: '#E11D48'
        }
      },
      steps: {
        path: 'Percurso',
        identity: 'Identidade',
        details: 'Detalhes',
        confirm: 'Confirmação'
      },
      form: {
        firstName: 'Nome',
        lastName: 'Apelido',
        email: 'Email',
        phone: 'Telefone',
        city: 'Cidade',
        country: 'País',
        profession: 'Profissão',
        skills: 'Competências',
        skillsPlaceholder: 'Ex: Comunicação, Design, Jurídico...',
        availability: 'Disponibilidade',
        availabilityOptions: ['Algumas horas/semana', 'Um dia/semana', 'Flexível', 'Apenas fins de semana'],
        motivation: 'Porque quer aderir à ADA?',
        motivationPlaceholder: 'Partilhe a sua motivação...',
        experience: 'Experiência associativa ou política',
        experiencePlaceholder: 'Descreva o seu percurso...',
        proposedCity: 'Cidade para criar o círculo',
        hasNetwork: 'Já tenho uma rede local para mobilizar',
        networkSize: 'Tamanho aproximado da rede',
        networkSizeOptions: ['5-10 pessoas', '10-25 pessoas', '25-50 pessoas', '50+ pessoas'],
        newsletter: 'Quero receber notícias da ADA',
        required: 'Campos obrigatórios'
      },
      buttons: {
        next: 'Continuar',
        back: 'Voltar',
        submit: 'Enviar a minha candidatura',
        submitting: 'A enviar...',
        choosePath: 'Escolher este percurso'
      },
      success: {
        title: 'Bem-vindo ao movimento!',
        subtitle: 'A sua candidatura foi registada',
        message: 'Um membro da equipa ADA irá contactá-lo nos próximos dias para discutir o seu compromisso.',
        cta: 'Voltar ao início',
        share: 'Partilhar o meu compromisso'
      },
      errors: {
        submit: 'Ocorreu um erro. Por favor, tente novamente.',
        required: 'Este campo é obrigatório'
      }
    }
  };

  const content = t[language] || t.fr;
  const paths = ['volunteer', 'circleCreator', 'ambassador', 'supporter'];

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handlePathSelect = (path) => {
    setSelectedPath(path);
    setCurrentStep(1);
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/api/members/join`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          path: selectedPath,
          language: language
        })
      });

      if (!response.ok) {
        throw new Error('Submission failed');
      }

      setIsSubmitted(true);
    } catch (err) {
      setError(content.errors.submit);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getStepCount = () => {
    if (selectedPath === 'supporter') return 2; // Just identity
    return 3; // Identity + Details
  };

  const canProceed = () => {
    if (currentStep === 1) {
      return formData.firstName && formData.lastName && formData.email && formData.city;
    }
    if (currentStep === 2) {
      if (selectedPath === 'circleCreator') {
        return formData.proposedCity && formData.motivation;
      }
      return formData.motivation;
    }
    return true;
  };

  // Render path selection (Step 0)
  const renderPathSelection = () => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="max-w-4xl mx-auto"
    >
      <div className="text-center mb-12">
        <h2 className={`text-3xl font-black mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`} style={{ fontFamily: 'var(--font-heading)' }}>
          {content.pageSubtitle}
        </h2>
      </div>

      <div className="grid sm:grid-cols-2 gap-6">
        {paths.map((pathKey, index) => {
          const path = content.paths[pathKey];
          const Icon = path.icon;
          
          return (
            <motion.button
              key={pathKey}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              onClick={() => handlePathSelect(pathKey)}
              className={`group relative p-6 rounded-xl border-2 text-left transition-all hover:-translate-y-1 ${
                isDark 
                  ? 'bg-[#1A0F2E] border-white/10 hover:border-white/30' 
                  : 'bg-white border-gray-200 hover:border-gray-300 hover:shadow-xl'
              }`}
              data-testid={`path-${pathKey}`}
            >
              <div 
                className="w-14 h-14 rounded-xl flex items-center justify-center mb-4 transition-transform group-hover:scale-110"
                style={{ backgroundColor: `${path.color}20` }}
              >
                <Icon className="w-7 h-7" style={{ color: path.color }} />
              </div>
              
              <h3 className={`text-xl font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {path.title}
              </h3>
              
              <p className={`text-sm mb-4 ${isDark ? 'text-white/60' : 'text-gray-600'}`}>
                {path.desc}
              </p>
              
              <div className="flex items-center gap-2 text-sm font-semibold" style={{ color: path.color }}>
                {content.buttons.choosePath}
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </div>
            </motion.button>
          );
        })}
      </div>
    </motion.div>
  );

  // Render identity form (Step 1)
  const renderIdentityForm = () => (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="max-w-2xl mx-auto"
    >
      <div className="grid sm:grid-cols-2 gap-6">
        <div>
          <label className={`block text-sm font-semibold mb-2 ${isDark ? 'text-white/80' : 'text-gray-700'}`}>
            {content.form.firstName} *
          </label>
          <input
            type="text"
            name="firstName"
            value={formData.firstName}
            onChange={handleChange}
            required
            className={`w-full px-4 py-3 rounded-lg border transition-colors ${
              isDark 
                ? 'bg-white/5 border-white/10 text-white focus:border-[#F7C948]' 
                : 'bg-white border-gray-200 text-gray-900 focus:border-[#5B2D8E]'
            } outline-none`}
            data-testid="join-firstname"
          />
        </div>
        
        <div>
          <label className={`block text-sm font-semibold mb-2 ${isDark ? 'text-white/80' : 'text-gray-700'}`}>
            {content.form.lastName} *
          </label>
          <input
            type="text"
            name="lastName"
            value={formData.lastName}
            onChange={handleChange}
            required
            className={`w-full px-4 py-3 rounded-lg border transition-colors ${
              isDark 
                ? 'bg-white/5 border-white/10 text-white focus:border-[#F7C948]' 
                : 'bg-white border-gray-200 text-gray-900 focus:border-[#5B2D8E]'
            } outline-none`}
            data-testid="join-lastname"
          />
        </div>
      </div>

      <div className="mt-6">
        <label className={`block text-sm font-semibold mb-2 ${isDark ? 'text-white/80' : 'text-gray-700'}`}>
          {content.form.email} *
        </label>
        <input
          type="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          required
          className={`w-full px-4 py-3 rounded-lg border transition-colors ${
            isDark 
              ? 'bg-white/5 border-white/10 text-white focus:border-[#F7C948]' 
              : 'bg-white border-gray-200 text-gray-900 focus:border-[#5B2D8E]'
          } outline-none`}
          data-testid="join-email"
        />
      </div>

      <div className="grid sm:grid-cols-2 gap-6 mt-6">
        <div>
          <label className={`block text-sm font-semibold mb-2 ${isDark ? 'text-white/80' : 'text-gray-700'}`}>
            {content.form.phone}
          </label>
          <input
            type="tel"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            placeholder="+225 07 XX XX XX XX"
            className={`w-full px-4 py-3 rounded-lg border transition-colors ${
              isDark 
                ? 'bg-white/5 border-white/10 text-white focus:border-[#F7C948]' 
                : 'bg-white border-gray-200 text-gray-900 focus:border-[#5B2D8E]'
            } outline-none`}
            data-testid="join-phone"
          />
        </div>
        
        <div>
          <label className={`block text-sm font-semibold mb-2 ${isDark ? 'text-white/80' : 'text-gray-700'}`}>
            {content.form.city} *
          </label>
          <input
            type="text"
            name="city"
            value={formData.city}
            onChange={handleChange}
            required
            className={`w-full px-4 py-3 rounded-lg border transition-colors ${
              isDark 
                ? 'bg-white/5 border-white/10 text-white focus:border-[#F7C948]' 
                : 'bg-white border-gray-200 text-gray-900 focus:border-[#5B2D8E]'
            } outline-none`}
            data-testid="join-city"
          />
        </div>
      </div>

      <div className="mt-6">
        <label className={`block text-sm font-semibold mb-2 ${isDark ? 'text-white/80' : 'text-gray-700'}`}>
          {content.form.profession}
        </label>
        <input
          type="text"
          name="profession"
          value={formData.profession}
          onChange={handleChange}
          className={`w-full px-4 py-3 rounded-lg border transition-colors ${
            isDark 
              ? 'bg-white/5 border-white/10 text-white focus:border-[#F7C948]' 
              : 'bg-white border-gray-200 text-gray-900 focus:border-[#5B2D8E]'
          } outline-none`}
          data-testid="join-profession"
        />
      </div>

      <div className="mt-6 flex items-center gap-3">
        <input
          type="checkbox"
          id="newsletter"
          name="newsletter"
          checked={formData.newsletter}
          onChange={handleChange}
          className="w-5 h-5 rounded border-gray-300 text-[#5B2D8E] focus:ring-[#5B2D8E]"
          data-testid="join-newsletter"
        />
        <label htmlFor="newsletter" className={`text-sm ${isDark ? 'text-white/70' : 'text-gray-600'}`}>
          {content.form.newsletter}
        </label>
      </div>
    </motion.div>
  );

  // Render details form (Step 2)
  const renderDetailsForm = () => (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="max-w-2xl mx-auto"
    >
      {/* Volunteer / Ambassador fields */}
      {(selectedPath === 'volunteer' || selectedPath === 'ambassador') && (
        <>
          <div className="mb-6">
            <label className={`block text-sm font-semibold mb-2 ${isDark ? 'text-white/80' : 'text-gray-700'}`}>
              {content.form.skills}
            </label>
            <input
              type="text"
              name="skills"
              value={formData.skills}
              onChange={handleChange}
              placeholder={content.form.skillsPlaceholder}
              className={`w-full px-4 py-3 rounded-lg border transition-colors ${
                isDark 
                  ? 'bg-white/5 border-white/10 text-white focus:border-[#F7C948]' 
                  : 'bg-white border-gray-200 text-gray-900 focus:border-[#5B2D8E]'
              } outline-none`}
              data-testid="join-skills"
            />
          </div>

          <div className="mb-6">
            <label className={`block text-sm font-semibold mb-2 ${isDark ? 'text-white/80' : 'text-gray-700'}`}>
              {content.form.availability}
            </label>
            <div className="grid sm:grid-cols-2 gap-3">
              {content.form.availabilityOptions.map((option, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, availability: option }))}
                  className={`px-4 py-3 rounded-lg border text-left text-sm transition-all ${
                    formData.availability === option
                      ? 'border-[#5B2D8E] bg-[#5B2D8E]/10 text-[#5B2D8E]'
                      : isDark
                        ? 'border-white/10 text-white/70 hover:border-white/30'
                        : 'border-gray-200 text-gray-600 hover:border-gray-300'
                  }`}
                >
                  {option}
                </button>
              ))}
            </div>
          </div>
        </>
      )}

      {/* Circle Creator fields */}
      {selectedPath === 'circleCreator' && (
        <>
          <div className="mb-6">
            <label className={`block text-sm font-semibold mb-2 ${isDark ? 'text-white/80' : 'text-gray-700'}`}>
              {content.form.proposedCity} *
            </label>
            <input
              type="text"
              name="proposedCity"
              value={formData.proposedCity}
              onChange={handleChange}
              required
              className={`w-full px-4 py-3 rounded-lg border transition-colors ${
                isDark 
                  ? 'bg-white/5 border-white/10 text-white focus:border-[#F7C948]' 
                  : 'bg-white border-gray-200 text-gray-900 focus:border-[#5B2D8E]'
              } outline-none`}
              data-testid="join-proposed-city"
            />
          </div>

          <div className="mb-6">
            <div className="flex items-center gap-3 mb-4">
              <input
                type="checkbox"
                id="hasNetwork"
                name="hasNetwork"
                checked={formData.hasNetwork}
                onChange={handleChange}
                className="w-5 h-5 rounded border-gray-300 text-[#5B2D8E] focus:ring-[#5B2D8E]"
                data-testid="join-has-network"
              />
              <label htmlFor="hasNetwork" className={`text-sm font-medium ${isDark ? 'text-white/80' : 'text-gray-700'}`}>
                {content.form.hasNetwork}
              </label>
            </div>

            {formData.hasNetwork && (
              <div>
                <label className={`block text-sm font-semibold mb-2 ${isDark ? 'text-white/80' : 'text-gray-700'}`}>
                  {content.form.networkSize}
                </label>
                <div className="grid sm:grid-cols-2 gap-3">
                  {content.form.networkSizeOptions.map((option, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, networkSize: option }))}
                      className={`px-4 py-3 rounded-lg border text-left text-sm transition-all ${
                        formData.networkSize === option
                          ? 'border-[#0D9488] bg-[#0D9488]/10 text-[#0D9488]'
                          : isDark
                            ? 'border-white/10 text-white/70 hover:border-white/30'
                            : 'border-gray-200 text-gray-600 hover:border-gray-300'
                      }`}
                    >
                      {option}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {/* Motivation (for all except supporter) */}
      <div className="mb-6">
        <label className={`block text-sm font-semibold mb-2 ${isDark ? 'text-white/80' : 'text-gray-700'}`}>
          {content.form.motivation} *
        </label>
        <textarea
          name="motivation"
          value={formData.motivation}
          onChange={handleChange}
          required
          rows={4}
          placeholder={content.form.motivationPlaceholder}
          className={`w-full px-4 py-3 rounded-lg border transition-colors resize-none ${
            isDark 
              ? 'bg-white/5 border-white/10 text-white focus:border-[#F7C948]' 
              : 'bg-white border-gray-200 text-gray-900 focus:border-[#5B2D8E]'
          } outline-none`}
          data-testid="join-motivation"
        />
      </div>

      {/* Experience (optional) */}
      <div>
        <label className={`block text-sm font-semibold mb-2 ${isDark ? 'text-white/80' : 'text-gray-700'}`}>
          {content.form.experience}
        </label>
        <textarea
          name="experience"
          value={formData.experience}
          onChange={handleChange}
          rows={3}
          placeholder={content.form.experiencePlaceholder}
          className={`w-full px-4 py-3 rounded-lg border transition-colors resize-none ${
            isDark 
              ? 'bg-white/5 border-white/10 text-white focus:border-[#F7C948]' 
              : 'bg-white border-gray-200 text-gray-900 focus:border-[#5B2D8E]'
          } outline-none`}
          data-testid="join-experience"
        />
      </div>
    </motion.div>
  );

  // Render success
  const renderSuccess = () => {
    const pathInfo = content.paths[selectedPath];
    const Icon = pathInfo.icon;
    
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="max-w-lg mx-auto text-center"
      >
        <div 
          className="w-24 h-24 mx-auto mb-6 rounded-full flex items-center justify-center"
          style={{ backgroundColor: `${pathInfo.color}20` }}
        >
          <CheckCircle2 className="w-12 h-12" style={{ color: pathInfo.color }} />
        </div>
        
        <h2 className={`text-3xl font-black mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`} style={{ fontFamily: 'var(--font-heading)' }}>
          {content.success.title}
        </h2>
        
        <p className="text-[#5B2D8E] font-semibold mb-4">
          {pathInfo.title}
        </p>
        
        <p className={`mb-8 ${isDark ? 'text-white/70' : 'text-gray-600'}`}>
          {content.success.message}
        </p>
        
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <Link
            to="/accueil"
            className="inline-flex items-center gap-2 px-6 py-3 bg-[#F7C948] text-[#0A0510] font-bold rounded-lg hover:bg-[#E5A700] transition-all"
          >
            {content.success.cta}
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </motion.div>
    );
  };

  // Progress indicator
  const renderProgress = () => {
    if (currentStep === 0 || isSubmitted) return null;
    
    const steps = selectedPath === 'supporter' 
      ? [content.steps.path, content.steps.identity]
      : [content.steps.path, content.steps.identity, content.steps.details];
    
    return (
      <div className="flex items-center justify-center gap-2 mb-12">
        {steps.map((step, i) => (
          <React.Fragment key={i}>
            <div className="flex items-center gap-2">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all ${
                i < currentStep 
                  ? 'bg-[#0D9488] text-white' 
                  : i === currentStep
                    ? 'bg-[#5B2D8E] text-white'
                    : isDark ? 'bg-white/10 text-white/50' : 'bg-gray-200 text-gray-500'
              }`}>
                {i < currentStep ? <Check className="w-4 h-4" /> : i + 1}
              </div>
              <span className={`text-sm hidden sm:inline ${
                i <= currentStep 
                  ? isDark ? 'text-white' : 'text-gray-900' 
                  : isDark ? 'text-white/50' : 'text-gray-400'
              }`}>
                {step}
              </span>
            </div>
            {i < steps.length - 1 && (
              <div className={`w-12 h-0.5 ${
                i < currentStep 
                  ? 'bg-[#0D9488]' 
                  : isDark ? 'bg-white/10' : 'bg-gray-200'
              }`} />
            )}
          </React.Fragment>
        ))}
      </div>
    );
  };

  return (
    <div className={`min-h-screen pt-20 ${isDark ? 'bg-[#0C0A09]' : 'bg-[#FAFAF9]'}`}>
      {/* Hero */}
      <section className="py-16 bg-gradient-to-br from-[#5B2D8E] to-[#7B4AAE] text-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#F7C948]/20 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-white/10 rounded-full blur-3xl" />
        
        <div className="container mx-auto px-4 lg:px-10 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex items-center justify-center gap-2 mb-4"
            >
              <Users className="w-6 h-6 text-[#F7C948]" />
              <span className="text-[#F7C948] text-sm font-bold uppercase tracking-widest">ADA</span>
            </motion.div>
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-4xl md:text-5xl font-black mb-4"
              style={{ fontFamily: 'var(--font-heading)' }}
            >
              {content.pageTitle}
            </motion.h1>
          </div>
        </div>
      </section>

      {/* Main content */}
      <section className="py-12 lg:py-16">
        <div className="container mx-auto px-4 lg:px-10">
          {renderProgress()}
          
          <AnimatePresence mode="wait">
            {isSubmitted ? (
              renderSuccess()
            ) : currentStep === 0 ? (
              renderPathSelection()
            ) : currentStep === 1 ? (
              renderIdentityForm()
            ) : (
              renderDetailsForm()
            )}
          </AnimatePresence>

          {/* Navigation buttons */}
          {!isSubmitted && currentStep > 0 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="max-w-2xl mx-auto mt-8 flex items-center justify-between"
            >
              <button
                onClick={() => setCurrentStep(prev => prev - 1)}
                className={`flex items-center gap-2 px-6 py-3 rounded-lg font-semibold transition-colors ${
                  isDark 
                    ? 'text-white/70 hover:text-white hover:bg-white/10' 
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                <ArrowLeft className="w-5 h-5" />
                {content.buttons.back}
              </button>

              {error && (
                <p className="text-red-500 text-sm">{error}</p>
              )}

              {((selectedPath === 'supporter' && currentStep === 1) || currentStep === 2) ? (
                <button
                  onClick={handleSubmit}
                  disabled={!canProceed() || isSubmitting}
                  className="flex items-center gap-2 px-8 py-3 bg-[#F7C948] text-[#0A0510] font-bold rounded-lg hover:bg-[#E5A700] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="join-submit"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      {content.buttons.submitting}
                    </>
                  ) : (
                    <>
                      {content.buttons.submit}
                      <Check className="w-5 h-5" />
                    </>
                  )}
                </button>
              ) : (
                <button
                  onClick={() => setCurrentStep(prev => prev + 1)}
                  disabled={!canProceed()}
                  className="flex items-center gap-2 px-8 py-3 bg-[#5B2D8E] text-white font-bold rounded-lg hover:bg-[#4A2472] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="join-next"
                >
                  {content.buttons.next}
                  <ArrowRight className="w-5 h-5" />
                </button>
              )}
            </motion.div>
          )}
        </div>
      </section>
    </div>
  );
};

export default JoinPage;
